"use client";

import { useState, useCallback, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { parseNaturalLanguageQuery } from "@/app/actions/natural-language-search";
import type { ParsedQuery } from "@/types/natural-language-search";

interface SimpleSearchBarProps {
  onSearch: (query: string, parsedQuery: ParsedQuery) => void;
  initialQuery?: string;
  placeholder?: string;
  isSearching?: boolean;
  className?: string;
  autoFocus?: boolean;
  style?: React.CSSProperties;
}

export function SimpleSearchBar({
  onSearch,
  initialQuery = "",
  placeholder = "Search properties...",
  isSearching = false,
  className,
  autoFocus = false,
  style,
}: SimpleSearchBarProps) {
  const [query, setQuery] = useState(initialQuery);
  const [parsedQuery, setParsedQuery] = useState<ParsedQuery | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;

    try {
      const fullParsedQuery = await parseNaturalLanguageQuery(query, {
        geocodeLocations: true,
      });
      setParsedQuery(fullParsedQuery);
      onSearch(query, fullParsedQuery);
    } catch (error) {
      console.error("[SEARCH] Error:", error);
      if (parsedQuery) {
        onSearch(query, parsedQuery);
      }
    }
  }, [query, parsedQuery, onSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    setQuery("");
    setParsedQuery(null);
    inputRef.current?.focus();
  };

  return (
    <div className={cn("relative min-w-3", className)}>
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="h-11 border-input bg-background pl-10 pr-10"
          />
          {query && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          onClick={handleSearch}
          disabled={!query.trim() || isSearching}
          className="h-11 px-6"
        >
          {isSearching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Search"
          )}
        </Button>
      </div>
    </div>
  );
}
